import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { addFullscreenChangeListeners } from '@/utils/fullscreen';
import { clientPointToElement, isInsideRotatedReader } from '@/utils/readerViewportCoordinates';

/** 控制条与书本底边的间距（PC） */
const CONTROLS_CANVAS_GAP = 30;
/** 控制条与书本底边的间距（移动端横屏，收紧以减少黑边） */
const CONTROLS_CANVAS_GAP_COMPACT = 12;

/** 控制条贴底时的底部边距（PC） */
const CONTROLS_BOTTOM_MARGIN = 5;
/** 控制条贴底时的底部边距（移动端横屏） */
const CONTROLS_BOTTOM_MARGIN_COMPACT = 8;

type ElementLike = HTMLElement | { $el: HTMLElement } | null;
type ReadonlyRef<T> = {
  readonly value: T;
};

/**
 * 解析组件 ref 或 DOM ref，得到实际 HTMLElement
 */
function resolveElement(target: ElementLike): HTMLElement | null {
  if (!target) return null;
  if (target instanceof HTMLElement) return target;
  if ('$el' in target && target.$el instanceof HTMLElement) return target.$el;
  return null;
}

/**
 * 计算绘本控制条位置：优先贴在书本下方，空间不足时贴底
 *
 * @param contentBottomRef 书本实际底边相对 canvas 元素顶部的距离（CSS px）。
 *   canvas 元素铺满视口，而书本居中渲染并不占满，故需用此值定位，
 *   传 null 时回退到 canvas 元素底部。
 * @param compactRef 为 true 时使用更紧凑的间距（移动端横屏）
 */
export function useBookControlsPosition(
  containerRef: ReadonlyRef<HTMLElement | null>,
  canvasRef: ReadonlyRef<HTMLElement | null>,
  controlsRef: ReadonlyRef<ElementLike>,
  recalcTrigger?: ReadonlyRef<unknown>,
  contentBottomRef?: ReadonlyRef<number | null>,
  compactRef?: ReadonlyRef<boolean>
) {
  const layoutVersion = ref(0);

  /** 触发位置重算 */
  const bumpLayout = () => {
    layoutVersion.value += 1;
  };

  let resizeObserver: ResizeObserver | null = null;
  let removeFullscreenChangeListeners: (() => void) | null = null;

  /** 监听容器、canvas、控制条尺寸变化 */
  const observeTargets = () => {
    resizeObserver?.disconnect();
    resizeObserver = new ResizeObserver(bumpLayout);

    for (const target of [containerRef.value, canvasRef.value, resolveElement(controlsRef.value)]) {
      if (target) resizeObserver.observe(target);
    }
  };

  const controlsPositionStyle = computed(() => {
    // 建立对 layoutVersion / recalcTrigger / contentBottom / compact 的响应式依赖
    void layoutVersion.value;
    void recalcTrigger?.value;
    const contentBottom = contentBottomRef?.value ?? null;
    const compact = compactRef?.value ?? false;
    const canvasGap = compact ? CONTROLS_CANVAS_GAP_COMPACT : CONTROLS_CANVAS_GAP;
    const bottomMargin = compact ? CONTROLS_BOTTOM_MARGIN_COMPACT : CONTROLS_BOTTOM_MARGIN;

    const fallback = {
      bottom: `${bottomMargin}px`,
      top: 'auto'
    };

    const container = containerRef.value;
    const canvas = canvasRef.value;
    const controls = resolveElement(controlsRef.value);

    if (!container || !canvas) return fallback;

    const canvasRect = canvas.getBoundingClientRect();
    const controlsHeight = controls?.offsetHeight ?? 0;
    const canvasLogicalOrigin = clientPointToElement(
      container,
      isInsideRotatedReader(container) ? canvasRect.right : canvasRect.left,
      canvasRect.top,
      container.clientWidth,
      container.clientHeight
    );

    // 步骤 1：计算书本底部相对容器顶部的位置。
    // 书本居中渲染、不占满 canvas，优先用 contentBottom（相对 canvas 顶部），
    // 缺省时回退到 canvas 元素底部。
    const bookBottomRelative =
      contentBottom != null
        ? canvasLogicalOrigin.y + contentBottom
        : canvasLogicalOrigin.y + canvas.offsetHeight;
    const preferredTop = bookBottomRelative + canvasGap;

    // 步骤 2：计算贴底时允许的最大 top，避免超出可视区域
    const maxTop = container.clientHeight - controlsHeight - bottomMargin;

    // 步骤 3：空间足够则跟在书本下方，否则贴底（取整避免亚像素发糊）
    if (controlsHeight > 0 && preferredTop <= maxTop) {
      return {
        top: `${Math.round(preferredTop)}px`,
        bottom: 'auto'
      };
    }

    return {
      bottom: `${bottomMargin}px`,
      top: 'auto'
    };
  });

  onMounted(() => {
    observeTargets();
    window.addEventListener('resize', bumpLayout);
    removeFullscreenChangeListeners = addFullscreenChangeListeners(document, bumpLayout);
  });

  watch([containerRef, canvasRef, controlsRef], () => {
    observeTargets();
    bumpLayout();
  });

  if (recalcTrigger) {
    watch(recalcTrigger, async () => {
      await nextTick();
      observeTargets();
      bumpLayout();
    });
  }

  // 横屏紧凑模式切换时重算间距
  if (compactRef) {
    watch(compactRef, bumpLayout);
  }

  onUnmounted(() => {
    resizeObserver?.disconnect();
    window.removeEventListener('resize', bumpLayout);
    removeFullscreenChangeListeners?.();
    removeFullscreenChangeListeners = null;
  });

  return { controlsPositionStyle };
}
